
import { HttpClient } from '@angular/common/http';
import { inject } from '@angular/core';
import { Observable,  of as observableOf, defer, EMPTY } from 'rxjs';
import { shareReplay, switchMap, map, toArray, expand, concatMap } from 'rxjs/operators';
import { EnvironmentService } from './environment.service';
import { BeachyAttributes } from '../../objects/beachy-object';

interface PagedRequestPayload {
  url: string;
  params: {[param: string]: string};
  mapTo?: (attributes: any) => any;
  page?: number;
  limit?: number;
}

export abstract class BaseService {
  protected http = inject(HttpClient);
  private environmentService = inject(EnvironmentService);

  protected baseUrl: string;
  protected hostname: string;
  protected url: string | null = null;

  protected abstract createInstance: (attributes: any) => any;

  constructor() {
    const environment = this.environmentService.resolve();

    if (environment.envBaseUrl) {
      this.baseUrl = environment.envBaseUrl;
    } else {
      switch(environment.envName) {
        case 'prod':
          this.baseUrl = 'https://api.beachyapp.com/api';
          break;

        case 'staging':
          this.baseUrl = 'https://staging.beachyapp.com/api';
          break;

        case 'acceptance':
          this.baseUrl = 'https://acceptance.beachyapp.com/api';
          break;

        default:
          this.baseUrl = 'http://localhost:9292/api';
          break;
      }
    }

    /**
     * QA-per-PR API base override. Two gates: envName must be 'acceptance'
     * AND the environment file must opt in via qaPerPrEnabled. Prod/staging/
     * staging builds never read localStorage here, so a file-replacement
     * misconfig that drops an acceptance envName into another build cannot
     * silently redirect API traffic.
     */
    if (environment.envName === 'acceptance' && environment.qaPerPrEnabled === true) {
      try {
        const qaPerPRURL = localStorage.getItem('pierogi_is_already_plural');
        if ((qaPerPRURL?.length ?? -1) > 0) {
          this.baseUrl = qaPerPRURL!;
        }
      } catch {
        console.error('No access to local storage granted, some of the widget functionalities will be limited.');
      }
    }

    this.hostname = new URL(window.location.href).hostname.split('.')[0];
  }

  getPage<TAttributes extends BeachyAttributes, T extends BeachyAttributes>({
    url,
    params,
    mapTo = this.createInstance,
    page = 1,
    limit = 20,
  }: PagedRequestPayload): Observable<{nextPage: number | null; results: T[]}> {
    return this.http.get<TAttributes[]>(url, {
      params: { ...params, limit: limit.toString(), page: page.toString() },
    })
    .pipe(
      map((resp: TAttributes[]) => {
        const results = resp.map(r => mapTo(r) as T);
        const nextPage = (results.length < limit) ? null : (page + 1);

        return {
          results,
          nextPage,
        };
      }),
    );
  }

  getPagedRequest<TAttributes extends BeachyAttributes, T extends BeachyAttributes>({
    url,
    params,
    mapTo = this.createInstance,
    page = 1,
    limit = 20,
  }: PagedRequestPayload): Observable<T[]> {
    return defer(() => this.getPage<TAttributes, T>({ url, params, mapTo, page, limit })
      .pipe(
        expand(({ nextPage }) => nextPage ? this.getPage<TAttributes, T>({ url, params, mapTo, page: nextPage, limit }) : EMPTY ),
        concatMap(({ results }) => results),
        toArray(),
      ),
    );
  }

  private callHttp(method: string, uri: string, payload: unknown): Observable<unknown> {
    switch (method) {
      case 'get': return this.http.get(uri);
      case 'post': return this.http.post(uri, payload);
      case 'put': return this.http.put(uri, payload);
      case 'delete': return this.http.delete(uri);
      default: throw new Error(`Unsupported HTTP method: ${method}`);
    }
  }

  call<P, S, R>(
    method: string,
    url: string|number,
    subscriber?: (item: S) => void,
    payload: P | null = null
  ): Observable<R> {
    const uri = `${this.baseUrl}/${url}`;

    const call$ = this.callHttp(method, uri, payload).pipe(
      switchMap(x => {
        if (x instanceof Array) {
          return observableOf(x.map(y => this.createInstance(y)));
        } else {
          return observableOf(this.createInstance(x));
        }
      }),
      shareReplay({ bufferSize: 1, refCount: true }),
    );

    if (subscriber) {
      call$.subscribe(subscriber);
    }

    return call$;
  }

  doGet<S, T>(url: string|number, subscriber?: (item: S) => void, queryObject?: any): Observable<T> {
    return this.call('get', url, subscriber, queryObject);
  }

  protected doGetAll<T>(url?: string|number, subscriber?: ((item: T) => void) | null, queryObject?: any): Observable<T>;
  protected doGetAll<S, T>(url: string|number, subscriber?: (item: S) => void, queryObject?: any): Observable<T> {
    return this.call('get', url, subscriber, queryObject);
  }

  protected doCreate<P, S, T>(payload: P | null, path: string, subscriber?: (item: S) => void): Observable<T> {
    return this.call('post', path, subscriber, payload);
  }

  protected doDelete<S, T>(path: string, subscriber?: (item: S) => void): Observable<T> {
    return this.call('delete', path, subscriber);
  }
}
